"use client"; // Mark component as client-side

import React from 'react';
import { MegaphoneOff } from 'lucide-react';
import Image from 'next/image'; // Import next/image
import { motion } from 'framer-motion'; // Import motion
import { Activity, Banknote, GlobeLock } from 'lucide-react';

const problems = [
  { icon: Banknote, text: 'Hiring editors is expensive', bgColor: '#FEEAF0' },
  { icon: Activity, text: 'Editing is time-consuming', bgColor: '#EAEBFD' },
  { icon: GlobeLock, text: 'Editing is difficult to learn', bgColor: '#DDFBFE' },
  { icon: Activity, text: 'Editors don\'t scale with cost', bgColor: '#DFF8EE' },
  { icon: GlobeLock, text: 'Regular ads don\'t get views', bgColor: '#EAEBFD' }, 
  { icon: Activity, text: 'Regular ads are hard to make', bgColor: '#DFF8EE' }, 
  { icon: Banknote, text: 'Do I have to hire an editing team?', bgColor: '#FEE9FB' },
  { icon: GlobeLock, text: 'How do I run a content campaign?', bgColor: '#DDFBFE' },
  { icon: Activity, text: 'How do I reach out to streamers?', bgColor: '#F5FFBD' },
  { icon: GlobeLock, text: 'How do I guarantee views?', bgColor: '#DDFBFE' }, 
  { icon: Banknote, text: 'Do I manually pay creators?', bgColor: '#DFF8EE' },
  { icon: Banknote, text: 'How do I organize payments?', bgColor: '#E8F1FD' },
  { icon: Activity, text: 'How do I track my ads?', bgColor: '#FEE9FB' },
  { icon: GlobeLock, text: 'How do I get creators to play my game?', bgColor: '#DFF8EE' }, 
  { icon: Banknote, text: 'Do I hire a content team?', bgColor: '#FEEDE1' },
  { icon: GlobeLock, text: 'How do I actually get people to care?', bgColor: '#E8F1FD' }, 
];

// Define sequence for staggering within groups
const intraGroupDelaySequence = [0, 0.4, 0.1, 1, 0.2]; // 5 values for group size 5

const groupSize = 5;
const duration = 6; // Increased duration
const groupCycleSeparation = 4; // Increased separation between group starts

// Shuffled assignment of items (index) to animation groups (value: 0-3)
const shuffledGroupAssignments = [
    0, 1, 2, 0, 3, // Items 0-4
    1, 0, 2, 1, 3, // Items 5-9
    0, 2, 1, 3, 2, // Items 10-14
    3                 // Item 15
];

// Pre-calculate the order of items within each shuffled group for intra-group delay
const groupMemberIndices = shuffledGroupAssignments.reduce((acc, groupIdx, originalIdx) => {
    if (!acc[groupIdx]) {
        acc[groupIdx] = [];
    }
    acc[groupIdx].push(originalIdx);
    return acc;
}, [] as number[][]);

export default function Problem() {
  return (
    // Outer container with background and padding
    <div className="bg-black py-16 sm:py-24">
      {/* Max-width container to center content */}
      <div className="">

        {/* Top Content Section (Headline, Subtext) */}
        <div className="text-center mb-12">
            <p className="text-sm font-semibold text-white tracking-wide mb-6 inline-block border border-white/20 rounded-full px-4 py-2">
              <span className="inline-flex items-center">
                <MegaphoneOff className="w-4 h-4 mr-2" />
                The problem
              </span>
            </p>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-semibold text-white mb-4">
                Organic Ads don't scale
            </h1>
        </div>

        {/* Problem Items Grid */}
        <div className="relative flex justify-center overflow-x-hidden pb-[66px] md:pb-8 pt-[20px] md:pt-[33px] max-w-[1512px] before:absolute before:inset-y-0 before:left-0 before:w-20 md:before:w-32 before:bg-gradient-to-r before:from-black before:to-transparent before:z-10 before:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:w-20 md:after:w-32 after:bg-gradient-to-l after:from-black after:to-transparent after:z-10 after:pointer-events-none">
          <div className="w-max">
            <div className="w-[720px] md:w-[1512px] flex flex-wrap gap-4 md:gap-6 justify-center">
              {problems.map((problem, index) => {
                // Get the pre-assigned group index for this item
                const groupIndex = shuffledGroupAssignments[index];

                // Find the item's position *within* its assigned shuffled group
                const indexWithinShuffledGroup = groupMemberIndices[groupIndex].indexOf(index);

                // Calculate base delay for the group using fixed separation
                const baseDelay = groupIndex * groupCycleSeparation;

                // Calculate stagger using the position within the *shuffled* group
                const intraGroupDelay = intraGroupDelaySequence[indexWithinShuffledGroup % intraGroupDelaySequence.length];

                // Calculate final delay
                const finalDelay = baseDelay + intraGroupDelay;

                return (
                  // Outer div for card structure and styling (non-animated)
                  <div key={index} className="w-[168px] bg-[#0b0b0c] rounded-2xl px-5 pb-4 pt-[34px] relative text-white border border-[#27272a] shadow-lg shadow-[#dddddd]/10">
                    {/* Animated Icon background circle */}
                    <motion.div
                      style={{ backgroundColor: problem.bgColor }}
                      className="mx-auto mb-8 w-[72px] h-[44px] rounded-full opacity-80"
                      initial={{ opacity: 0, y: 10, filter: 'blur(0.5px)' }}
                      animate={{
                          opacity: [0, 1, 1, 0],
                          y: [10, 0, 0, -10],
                          filter: ['blur(1px)', 'blur(0px)', 'blur(0px)', 'blur(1px)'],
                      }}
                      transition={{
                          duration: duration,
                          repeat: Infinity,
                          repeatType: 'loop',
                          delay: finalDelay,
                          ease: "easeInOut",
                          times: [0, 0.05, 0.7, 0.8]
                      }}
                    />
                    {/* Animated Icon container */}
                    <motion.div
                      className="w-8 h-8 top-[40px] left-[68px] absolute flex items-center justify-center"
                      initial={{ opacity: 0, y: 10, filter: 'blur(0.5px)' }}
                      animate={{
                          opacity: [0, 1, 1, 0],
                          y: [10, 0, 0, -10],
                          filter: ['blur(0.5px)', 'blur(0px)', 'blur(0px)', 'blur(0.5px)'],
                      }}
                      transition={{
                          duration: duration,
                          repeat: Infinity,
                          repeatType: 'loop',
                          delay: finalDelay,
                          ease: "easeInOut",
                          times: [0, 0.05, 0.7, 0.8]
                      }}
                      >
                      {React.createElement(problem.icon, {
                        size:18,
                        strokeWidth: 2,
                        className: "text-black"
                      })}
                    </motion.div>
                    {/* Animated Label using motion.div */}
                    <motion.div
                      className="text-center text-sm font-medium"
                      initial={{ opacity: 0, y: 10, filter: 'blur(0.5px)' }}
                      animate={{
                          opacity: [0, 1, 1, 0],
                          y: [10, 0, 0, -10],
                          filter: ['blur(0.5px)', 'blur(0px)', 'blur(0px)', 'blur(0.5px)'],
                      }}
                      transition={{
                          duration: duration,
                          repeat: Infinity,
                          repeatType: 'loop',
                          delay: finalDelay,
                          ease: "easeInOut",
                          times: [0, 0.05, 0.7, 0.8]
                      }}
                      dangerouslySetInnerHTML={{ __html: problem.text }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
} 